import { supabase } from '../lib/supabaseClient';
import { isDemoMode } from '../demo/demoMode';
import { getUserInfo } from '../utils/tokenManager';
import { getAuthInfo } from './authService';

/* 전도금(지출) 비고 수정본 (Supabase pre_deposit_remark)
 * ===========================================================================
 * ERP 는 결재 상신된 전도금의 비고 수정을 막는다. 그래서 고친 비고만
 * 세부내역번호를 키로 Supabase 에 따로 두고, 화면이 ERP 비고 대신 이 값을 보여준다.
 * - 조회 실패/미설정이면 빈 Map → ERP 원래 비고가 그대로 보인다 (화면 흐름은 막지 않음).
 * - 데모 모드에서는 Supabase 로 나가지 않는다 (저장은 화면 상태로만).
 */

const toNo = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** 여러 전도금의 수정 비고를 한 번에 읽는다. → Map<세부내역번호(number), row> */
export const getPreDepositRemarks = async (detailNos = []) => {
  const result = new Map();
  if (isDemoMode() || !supabase) return result;
  const nos = Array.from(new Set((detailNos || []).map(toNo).filter(Boolean)));
  if (!nos.length) return result;

  const CHUNK = 200;
  for (let i = 0; i < nos.length; i += CHUNK) {
    const { data, error } = await supabase
      .from('pre_deposit_remark')
      .select('세부내역번호, 비고, 원래비고, 수정자, updated_at')
      .in('세부내역번호', nos.slice(i, i + CHUNK));
    if (error) {
      console.warn('[preDepositRemark] 조회 실패(무시):', error.message);
      return result;
    }
    (data || []).forEach((row) => result.set(Number(row.세부내역번호), row));
  }
  return result;
};

/**
 * 수정 비고 저장 (upsert). 실패 시 throw.
 * @returns 저장된 row (데모 모드면 저장 없이 만든 row)
 */
export const savePreDepositRemark = async ({ detailNo, groupNo, remark, originalRemark }) => {
  const no = toNo(detailNo);
  if (!no) throw new Error('세부내역번호가 없어 저장할 수 없습니다.');

  const { userNo, companyNo, email } = getUserInfo() || {};
  const loginId = getAuthInfo()?.userName || '';
  const row = {
    세부내역번호: no,
    단체번호: toNo(groupNo),
    공급업체번호: toNo(companyNo),
    비고: String(remark ?? ''),
    원래비고: originalRemark == null ? null : String(originalRemark),
    수정자번호: String(userNo || ''),
    수정자: loginId || email || '',
    updated_at: new Date().toISOString(),
  };

  if (isDemoMode()) return row;
  if (!supabase) throw new Error('Supabase 설정(REACT_APP_SUPABASE_URL / ANON_KEY)이 없어 저장할 수 없습니다.');

  const { error } = await supabase.from('pre_deposit_remark').upsert(row, { onConflict: '세부내역번호' });
  if (error) throw new Error(`비고 저장 실패: ${error.message}`);
  return row;
};

/** 전도금이 ERP 에서 삭제될 때 함께 지운다. 실패해도 무시 (남아도 화면에 영향 없음). */
export const deletePreDepositRemark = async (detailNo) => {
  const no = toNo(detailNo);
  if (!no || isDemoMode() || !supabase) return;
  const { error } = await supabase.from('pre_deposit_remark').delete().eq('세부내역번호', no);
  if (error) console.warn('[preDepositRemark] 삭제 실패(무시):', error.message);
};
